import { reactive, readonly } from 'vue'
import { useGoogleMapsLoader } from '@/composables/google-maps-loader'
import { useGoogleMaps } from '@/composables/google-maps'
import { getAddressObject, isNullOrUndefined } from '@/utils'
import type { Address } from '@/utils'
import { WaypointType } from '@apiTypes'

export interface AutocompleteSuggestion {
  placePrediction: any
  displayText: string
}

export function useGoogleMapsAutocomplete() {
  const { reverseGeocode } = useGoogleMaps()
  const loadGoogleMapsScript = useGoogleMapsLoader()

  const state = reactive({
    showSuggestions: false,
    suggestions: [] as AutocompleteSuggestion[],
    selectedIndex: -1,
    isLoading: false,
    isInitialized: false,
  })

  let AutocompleteSuggestion: any = null
  let sessionToken: any = null

  // Debounced, request-id-guarded fetching. Places was being called on every
  // keystroke (gated only by isLoading), which silently dropped keystrokes that
  // arrived while a request was in flight, and had no protection against a
  // slower earlier response overwriting a faster later one -- together these
  // produced suggestions for a stale, truncated version of the typed text.
  let latestRequestId = 0
  let debounceTimer: ReturnType<typeof setTimeout> | null = null
  let pendingResolve: (() => void) | null = null

  const SUGGESTION_DEBOUNCE_MS = 150

  const googlePlaceFields = ['displayName', 'formattedAddress', 'location', 'addressComponents']

  const invalidatePendingRequests = (): void => {
    latestRequestId += 1

    // A superseded request must not report itself as still loading.
    state.isLoading = false

    if (debounceTimer) {
      clearTimeout(debounceTimer)
      debounceTimer = null
    }

    if (pendingResolve) {
      const resolve = pendingResolve

      pendingResolve = null
      resolve()
    }
  }

  const initialize = async (): Promise<boolean> => {
    if (state.isInitialized)
      return true

    try {
      await loadGoogleMapsScript

      const placesLibrary = await google.maps.importLibrary('places') as google.maps.PlacesLibrary

      AutocompleteSuggestion = placesLibrary.AutocompleteSuggestion
      sessionToken = new placesLibrary.AutocompleteSessionToken()
      state.isInitialized = true

      return true
    }
    catch (error) {
      console.error('Failed to initialize Google Maps autocomplete:', error)

      return false
    }
  }

  const performFetch = async (input: string, requestId: number): Promise<void> => {
    if (requestId !== latestRequestId)
      return

    try {
      state.isLoading = true

      const request = {
        input,
        sessionToken,
        locationRestriction: {
          north: 71.0,
          south: 36.0,
          east: 67.0,
          west: -10.0,
        },
      }

      const { suggestions: fetchedSuggestions } = await AutocompleteSuggestion.fetchAutocompleteSuggestions(request)

      if (requestId !== latestRequestId)
        return

      state.suggestions = fetchedSuggestions?.map((suggestion: any, i: number) => {
        try {
          const text = suggestion?.placePrediction?.text?.toString()

          if (!text) {
            console.error(`No text available for suggestion ${i}`)

            return null
          }

          return {
            placePrediction: suggestion.placePrediction,
            displayText: text,
          }
        }
        catch (e) {
          console.error(`Failed to extract text for suggestion ${i}:`, e)

          return null
        }
      }).filter(Boolean) || []

      state.showSuggestions = state.suggestions.length > 0
      state.selectedIndex = -1
    }
    catch (error) {
      if (requestId !== latestRequestId)
        return

      console.error('Error fetching suggestions:', error)
      state.showSuggestions = false
      state.suggestions = []
    }
    finally {
      if (requestId === latestRequestId)
        state.isLoading = false
    }
  }

  const fetchSuggestions = (input: string): Promise<void> => {
    invalidatePendingRequests()

    if (!AutocompleteSuggestion || input.trim().length <= 2) {
      state.showSuggestions = false
      state.suggestions = []

      return Promise.resolve()
    }

    const requestId = latestRequestId

    return new Promise<void>(resolve => {
      pendingResolve = resolve

      debounceTimer = setTimeout(() => {
        debounceTimer = null
        pendingResolve = null

        void performFetch(input, requestId).then(resolve)
      }, SUGGESTION_DEBOUNCE_MS)
    })
  }

  const setPostalCodeIfMissing = async (address: Address, lat: number, lng: number): Promise<void> => {
    if (address.postal_code)
      return

    try {
      address.postal_code = await reverseGeocode(lat, lng) || ''
    }
    catch (error) {
      console.warn('Failed to reverse geocode for postal code:', error)
    }
  }

  const setAddressLocation = async (address: Address, place: any): Promise<void> => {
    if (!place.location)
      return

    const lat = place.location.lat()
    const lng = place.location.lng()

    if (isNullOrUndefined(lat) || isNullOrUndefined(lng))
      return

    address.point = {
      type: WaypointType.Point,
      coordinates: [lng, lat],
    }

    await setPostalCodeIfMissing(address, lat, lng)
  }

  const createAddressFromPlace = async (place: any): Promise<Address> => {
    const address = getAddressObject(place.addressComponents || [])

    await setAddressLocation(address, place)

    // An area picked as such (a district, a town) is not a business: its name is one of the place's
    // political components, e.g. "Innere Stadt" for "Innere Stadt, Wien".
    const isAreaName = (place.addressComponents || []).some((component: any) =>
      component.types?.includes('political') && (component.longText || component.long_name) === place.displayName)

    // Only set name if it's a business/POI (has displayName) and different from extracted street
    if (place.displayName && !isAreaName && place.displayName !== address.street && place.displayName !== address.city)
      address.name = place.displayName

    // Only set street from formattedAddress if we don't already have a street from addressComponents
    // and it's not just a city-level address
    if (!address.street && place.formattedAddress
        && (!address.city || !place.formattedAddress.includes(`${address.city},`)))
      address.street = place.formattedAddress

    return address
  }

  const selectSuggestion = async (suggestion: AutocompleteSuggestion): Promise<{ text: string; address: Address | null }> => {
    invalidatePendingRequests()
    state.showSuggestions = false

    if (!suggestion?.placePrediction) {
      console.error('No placePrediction found in suggestion')

      return { text: suggestion.displayText || '', address: null }
    }

    try {
      const place = suggestion.placePrediction.toPlace()

      await place.fetchFields({ fields: googlePlaceFields })

      const inputText = place.formattedAddress || place.displayName || suggestion.displayText || ''
      const address = await createAddressFromPlace(place)

      return { text: inputText, address }
    }
    catch (error) {
      console.error('Error selecting place:', error)

      return { text: suggestion.displayText || '', address: null }
    }
  }

  const handleKeydown = (event: KeyboardEvent): boolean => {
    if (!state.showSuggestions || state.suggestions.length === 0)
      return false

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault()
        state.selectedIndex = Math.min(state.selectedIndex + 1, state.suggestions.length - 1)

        return true
      case 'ArrowUp':
        event.preventDefault()
        state.selectedIndex = Math.max(state.selectedIndex - 1, -1)

        return true
      case 'Enter':
        event.preventDefault()
        if (state.selectedIndex >= 0) {
          // This will be handled by the parent component
          return true
        }
        break
      case 'Escape':
        state.showSuggestions = false
        state.selectedIndex = -1

        return true
    }

    return false
  }

  const hideSuggestions = (): void => {
    invalidatePendingRequests()
    state.showSuggestions = false
    state.selectedIndex = -1
  }

  // Called while a suggestion is being pressed: a fetch still pending for newer text must not swap
  // the list under the pointer, or the click that follows would pick a different place.
  const holdSuggestions = (): void => {
    invalidatePendingRequests()
  }

  const showSuggestionsIfAvailable = (): void => {
    if (state.suggestions.length > 0)
      state.showSuggestions = true
  }

  const getSelectedSuggestion = (): AutocompleteSuggestion | null => {
    return state.selectedIndex >= 0 ? state.suggestions[state.selectedIndex] : null
  }

  return {
    state: readonly(state),
    initialize,
    fetchSuggestions,
    selectSuggestion,
    handleKeydown,
    hideSuggestions,
    holdSuggestions,
    showSuggestionsIfAvailable,
    getSelectedSuggestion,
  }
}
